
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function LandlordElectricalSafetyChecksPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Landlord Electrical Safety Checks',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Landlord electrical safety checks and rental property inspections in Fresno, CA. Ensure compliance with California habitability standards and protect your tenants.',
    url: `${siteUrl}/services/testing-certification/landlord-electrical-safety-checks`,
  };

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Tenant Safety Protection',
      description:
        'Ensure rental units meet California habitability standards and electrical safety requirements',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Compliance Documentation',
      description:
        'Detailed inspection reports for liability protection and regulatory compliance',
    },
    {
      icon: 'ri-home-4-line',
      title: 'Multi-Unit Inspections',
      description:
        'Efficient inspections for apartment buildings, duplexes, and rental property portfolios',
    },
    {
      icon: 'ri-alert-line',
      title: 'Hazard Identification',
      description:
        'Identify fire risks, shock hazards, and electrical code violations before tenant issues arise',
    },
    {
      icon: 'ri-tools-line',
      title: 'Between-Tenant Checks',
      description:
        'Electrical safety inspections during tenant turnover to document unit condition',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Scheduled Maintenance',
      description:
        'Regular inspection programs to maintain safety and prevent costly emergency repairs',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Liability Reduction',
      description:
        'Protect yourself from tenant injury claims and property damage lawsuits',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed Reports',
      description:
        'Comprehensive documentation with photos for insurance, legal, and tenant records',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician inspection',
    'Electrical panel and breaker assessment',
    'Outlet and switch functionality testing',
    'GFCI/AFCI protection verification',
    'Smoke detector and CO detector checks',
    'Lighting fixture inspection',
    'Grounding and bonding verification',
    'Code compliance documentation',
    'Safety hazard identification',
    'Detailed written report with photos',
  ];

  return (
    <>
      <SEOHead
        title="Landlord Electrical Safety Checks Fresno CA | Rental Property Electrical Inspections"
        description="Landlord electrical safety checks and rental property inspections in Fresno, CA. Ensure compliance, protect tenants, and reduce liability. Multi-unit inspections available."
        canonical="/services/testing-certification/landlord-electrical-safety-checks"
        keywords="landlord electrical inspection Fresno, rental property electrical safety, landlord safety check, Fresno rental inspection"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Landlord Electrical Safety Checks — Fresno, CA"
        heroSubtitle="Protect your tenants and your investment with professional rental property electrical inspections"
        heroImage="https://public.readdy.ai/ai/img_res/8b9fa3dea98ee6e5a32812befc9a20a9.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20landlord%20safety%20inspection%20checklist%20on%20clipboard%20next%20to%20digital%20multimeter%20and%20electrical%20testing%20tools%20on%20table%20in%20front%20of%20residential%20electrical%20panel%20in%20Fresno%20rental%20property%20bright%20interior&width=700&height=600&seq=landlord-fresno-001&orientation=portrait"
        fresnoContent="Fresno landlords and property managers trust us for thorough electrical safety checks that protect tenants and reduce liability. Whether you manage apartments in Tower District, rental homes in Woodward Park, or multi-unit properties throughout Fresno County, our licensed electricians ensure your rental units meet California habitability standards and electrical safety requirements. We provide detailed documentation for compliance, insurance, and tenant records."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'Why do Fresno landlords need electrical safety checks?',
            answer:
              'California law requires landlords to provide safe, habitable rental units. Electrical safety checks identify hazards, code violations, and maintenance issues before they cause tenant injuries, fires, or lawsuits. Regular inspections protect your Fresno tenants, reduce liability, maintain insurance coverage, and help you comply with California Civil Code habitability requirements.',
          },
          {
            question: 'How much do landlord electrical safety checks cost in Fresno?',
            answer:
              'Rental property electrical inspections in Fresno typically cost $150–$300 per unit depending on size and complexity. Multi-unit properties receive discounted rates. We offer scheduled inspection programs for property managers with multiple Fresno rental properties. All inspections include detailed written reports for your records.',
          },
          {
            question: 'How often should I get electrical safety checks for my Fresno rental properties?',
            answer:
              'We recommend electrical safety checks between every tenant turnover, annually for long-term tenants, and immediately if tenants report electrical problems. California law requires landlords to maintain safe electrical systems, and regular inspections help you identify issues before they become dangerous or costly for your Fresno rental properties.',
          },
          {
            question: 'What does a landlord electrical safety check in Fresno include?',
            answer:
              'Our Fresno landlord inspections cover electrical panels, circuit breakers, outlets, switches, GFCI/AFCI protection, lighting fixtures, smoke detectors, CO detectors, grounding systems, and overall code compliance. We identify safety hazards, maintenance needs, and code violations. You receive a detailed report with photos for tenant files and insurance records.',
          },
          {
            question: 'Do you inspect multi-unit properties and apartment buildings in Fresno?',
            answer:
              'Yes. We provide efficient electrical safety inspections for apartment buildings, duplexes, triplexes, and rental property portfolios throughout Fresno. We offer volume discounts for multi-unit properties and can schedule inspections to minimize disruption to tenants. Our reports help property managers maintain compliance across all units.',
          },
          {
            question: 'Can your inspection reports protect me from tenant liability claims in Fresno?',
            answer:
              'Yes. Our detailed inspection reports document the electrical condition of your rental units and demonstrate your compliance with California safety standards. If a tenant claims electrical problems caused injury or property damage, our reports provide evidence that you maintained safe conditions. This documentation is valuable for insurance claims and legal defense in Fresno.',
          },
        ]}
      />
    </>
  );
}
